/**
 * App manifests: the per-tenant overlay on the compiled graph registries.
 *
 * A manifest declares, in one JSON document, the object types (with fields), edge types, views, nav and picklists of an
 * app a tenant installs. Every type key is namespaced `app:<appKey>:<Type>` so it can never shadow a built-in
 * NODE_REGISTRY or EDGE_REGISTRY entry, and validateAppManifest rejects any key that does collide. Field definitions
 * reuse the FormFieldConfig type union the CRM already renders, so the generic list, detail and form views need no new
 * field renderers for an app type.
 */
import { z } from 'zod';
import type { FormFieldConfig } from '../graph/nodes/base-crm';
import { NODE_REGISTRY } from '../graph/nodes';
import { EDGE_REGISTRY } from '../graph/edges';
import { MODULE_REGISTRY } from '../graph/modules';
import { PersonaDefinitionSchema, SCOPE_ALL, validatePersonaDefinition, type PersonaDefinition } from '../personas/definition';
import { AppViewFilterSchema, AppViewSortSchema, viewRuleErrors } from './views';

/**
 * Apps Diggens installs and keeps itself, each with the agent whose work it shows (`collection` is reserved for the
 * Document Collection app). They are not in the catalog, nobody else drafts, activates or disables one, and only they may
 * have read-only types.
 */
export const MANAGED_APP_KEYS: readonly string[] = ['collection'];

/** Whether a node or edge type belongs to an app Diggens keeps (`app:<managed key>:…`). */
export function isManagedAppType(type: string): boolean {
  return MANAGED_APP_KEYS.some((key) => type.startsWith(`app:${key}:`));
}

export const APP_TYPE_PREFIX = 'app:';
/** An app key: lower-case, digits and hyphens, 2 to 40 characters. */
export const APP_KEY_PATTERN = /^[a-z][a-z0-9-]{1,39}$/;
/** An app's own type, `app:<appKey>:<Type>`, or a built-in type name the app redefines for the tenant. */
const OBJECT_TYPE_KEY_PATTERN = /^(app:[a-z][a-z0-9-]{1,39}:[A-Z][A-Za-z0-9]{0,63}|[A-Z][A-Za-z0-9]{0,63})$/;
const APP_TYPE_KEY_PATTERN = /^app:[a-z][a-z0-9-]{1,39}:[A-Z][A-Za-z0-9]{0,63}$/;
const EDGE_TYPE_KEY_PATTERN = /^app:[a-z][a-z0-9-]{1,39}:[A-Z][A-Z0-9_]{0,63}$/;
const FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
/** A field key, or a built-in virtual column such as `organization.name` that a list view fills from an include. */
const FORM_FIELD_KEY_PATTERN = /^[a-z][a-z0-9_]{0,63}(\.[a-z][a-z0-9_]{0,63})?$/;
const SLUG_PATTERN = /^[a-z][a-z0-9-]{0,63}$/;
const PICKLIST_NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
const PICKLIST_ITEM_ID_PATTERN = /^[a-z0-9][a-z0-9_-]{0,63}$/;
const ICON_PATTERN = /^[A-Z][A-Za-z0-9]{0,63}$/;
const HEX_COLOR_PATTERN = /^#[0-9a-fA-F]{6}$/;
const NAV_PATH_PATTERN = /^\/[a-z0-9-]+(\/[a-z0-9-]+)*$/;

/** Paths the CRM shell owns; an app nav item may not take them. */
export const RESERVED_NAV_PATHS: readonly string[] = ['/', '/home', '/login', '/signup', '/settings', '/analytics', '/data', '/lists', '/auth'];

export const APP_FIELD_TYPES = ['text', 'email', 'tel', 'url', 'number', 'date', 'datetime', 'select', 'textarea', 'richtext', 'json', 'geo', 'user', 'lookup'] as const satisfies readonly FormFieldConfig['type'][];

const LookupConfigSchema = z
  .object({
    nodeType: z.string().min(1),
    edgeType: z.string().min(1),
    direction: z.enum(['outgoing', 'incoming']),
    edgeProps: z.record(z.unknown()).optional(),
  })
  .strict();

export const AppFieldSchema = z
  .object({
    key: z.string().regex(FORM_FIELD_KEY_PATTERN, 'field keys are snake_case'),
    label: z.string().min(1).max(80),
    type: z.enum(APP_FIELD_TYPES),
    required: z.boolean().optional(),
    options: z.array(z.string().min(1)).min(1).max(100).optional(),
    picklistName: z.string().regex(PICKLIST_NAME_PATTERN).optional(),
    format: z.enum(['currency', 'percentage', 'year']).optional(),
    lookupConfig: LookupConfigSchema.optional(),
    newLine: z.boolean().optional(),
  })
  .strict()
  .superRefine((field, ctx) => {
    if (field.type === 'select' && !field.options && !field.picklistName) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `select field "${field.key}" needs options or a picklistName` });
    }
    if (field.type === 'lookup' && !field.lookupConfig) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `lookup field "${field.key}" needs a lookupConfig` });
    }
    if (field.format && field.type !== 'number') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `field "${field.key}": format applies to number fields only` });
    }
  });

const RelationshipColumnSchema = z
  .object({
    field: z.string().min(1),
    headerName: z.string().min(1),
    flex: z.number().positive().optional(),
    width: z.number().positive().optional(),
    minWidth: z.number().positive().optional(),
    type: z.enum(['string', 'number', 'date', 'datetime', 'boolean']).optional(),
    format: z.enum(['currency', 'percentage', 'year']).optional(),
    source: z.enum(['node', 'edge']).optional(),
    editable: z.boolean().optional(),
    editType: z.enum(['text', 'date', 'datetime', 'number', 'boolean', 'select', 'picklist']).optional(),
    options: z.array(z.string()).optional(),
    picklistName: z.string().regex(PICKLIST_NAME_PATTERN).optional(),
  })
  .strict();

export const AppRelationshipSectionSchema = z
  .object({
    sectionLabel: z.string().min(1).max(80),
    icon: z.string().regex(ICON_PATTERN),
    edgeTypes: z.array(z.string().min(1)).min(1),
    direction: z.enum(['outgoing', 'incoming', 'both']),
    relatedNodeTypes: z.array(z.string().min(1)).optional(),
    renderMode: z.enum(['card', 'grid']),
    primaryProp: z.string().optional(),
    secondaryProps: z.array(z.string()).optional(),
    columns: z.array(RelationshipColumnSchema).optional(),
  })
  .strict();

export const AppObjectTypeSchema = z
  .object({
    key: z.string().regex(OBJECT_TYPE_KEY_PATTERN, 'object type keys look like app:<appKey>:<TypeName>, or name a built-in type to redefine'),
    displayName: z.string().min(1).max(80),
    displayNamePlural: z.string().min(1).max(80),
    icon: z.string().regex(ICON_PATTERN, 'icon is a Lucide icon name such as FolderKanban'),
    color: z.string().regex(HEX_COLOR_PATTERN, 'color is a six-digit hex color'),
    description: z.string().max(500).optional(),
    fields: z.array(AppFieldSchema).min(1).max(60),
    naturalKeyField: z.string().regex(FIELD_KEY_PATTERN).optional(),
    relationshipSections: z.array(AppRelationshipSectionSchema).max(20).optional(),
    /**
     * Diggens itself keeps these records (a first-party app's sync): people and agents read them, and every way of
     * creating, editing or deleting a record refuses them. Only an app's own type can be read-only.
     */
    readOnly: z.literal(true).optional(),
  })
  .strict()
  .superRefine((objectType, ctx) => {
    if (objectType.readOnly && !objectType.key.startsWith('app:')) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${objectType.key}: only an app's own type can be read-only, never a built-in type` });
    }
    const keys = new Set<string>();
    for (const field of objectType.fields) {
      if (keys.has(field.key)) ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${objectType.key}: duplicate field "${field.key}"` });
      keys.add(field.key);
    }
    const title = objectType.fields[0];
    if (title && (title.type !== 'text' || !title.required)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${objectType.key}: the first field is the record title and must be a required text field` });
    }
    if (objectType.naturalKeyField && !keys.has(objectType.naturalKeyField)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `${objectType.key}: naturalKeyField "${objectType.naturalKeyField}" is not one of its fields` });
    }
  });

export const AppEdgePropSchema = z
  .object({
    key: z.string().regex(FIELD_KEY_PATTERN),
    label: z.string().min(1).max(80),
    type: z.enum(['text', 'date', 'number', 'boolean', 'select', 'picklist']),
    options: z.array(z.string().min(1)).optional(),
    picklistName: z.string().regex(PICKLIST_NAME_PATTERN).optional(),
  })
  .strict();

export const AppEdgeTypeSchema = z
  .object({
    key: z.string().regex(EDGE_TYPE_KEY_PATTERN, 'edge type keys look like app:<appKey>:UPPER_SNAKE'),
    displayName: z.string().min(1).max(80),
    reverseDisplayName: z.string().min(1).max(80).optional(),
    sourceTypes: z.array(z.string().min(1)).min(1),
    targetTypes: z.array(z.string().min(1)).min(1),
    props: z.array(AppEdgePropSchema).max(20).optional(),
  })
  .strict();

/** A related record shown on each kanban card, and offered as a filter above the board. */
export const AppCardRelationSchema = z
  .object({
    edgeType: z.string().min(1),
    direction: z.enum(['outgoing', 'incoming']),
    /** The related record's prop to show; its title field by default. */
    displayField: z.string().regex(FIELD_KEY_PATTERN).default('name'),
    label: z.string().min(1).max(40).optional(),
  })
  .strict();

export const AppViewSchema = z
  .object({
    key: z.string().regex(SLUG_PATTERN),
    objectType: z.string().min(1),
    kind: z.enum(['table', 'kanban']),
    columns: z.array(z.string().regex(FIELD_KEY_PATTERN)).min(1).max(30).optional(),
    stageField: z.string().regex(FIELD_KEY_PATTERN).optional(),
    picklistName: z.string().regex(PICKLIST_NAME_PATTERN).optional(),
    cardRelation: AppCardRelationSchema.optional(),
    /** Kanban only: a number field whose total each column shows in its header; with none, headers show no total. */
    sumField: z.string().regex(FIELD_KEY_PATTERN).optional(),
    /** The order rows (or cards in a column) open in; a field of the type, or created_at/updated_at. Readers may re-sort a table. */
    defaultSort: AppViewSortSchema.optional(),
    /**
     * Filters every reader gets and cannot remove, ANDed together and with whatever the reader filters on top. This is
     * how a view says "overdue" (due_date before today) or "waiting on the client" (status eq waiting_on_client).
     */
    filters: z.array(AppViewFilterSchema).min(1).max(10).optional(),
    /** Kanban only: select, user and date fields the reader may filter the board by, as controls above it. */
    boardFilters: z.array(z.string().regex(FIELD_KEY_PATTERN)).min(1).max(6).optional(),
  })
  .strict()
  .superRefine((view, ctx) => {
    if (view.boardFilters && view.kind !== 'kanban') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `view "${view.key}": boardFilters applies to kanban views only` });
    }
    if (view.sumField && view.kind !== 'kanban') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `view "${view.key}": sumField applies to kanban views only` });
    }
    if (view.kind === 'kanban' && (!view.stageField || !view.picklistName)) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `view "${view.key}": a kanban view needs stageField and picklistName` });
    }
    if (view.cardRelation && view.kind !== 'kanban') {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: `view "${view.key}": cardRelation applies to kanban views only` });
    }
  });

export const AppNavItemSchema = z
  .object({
    path: z.string().regex(NAV_PATH_PATTERN, 'nav paths are lower-case segments such as /projects'),
    label: z.string().min(1).max(60),
    icon: z.string().regex(ICON_PATTERN),
    objectType: z.string().min(1),
    viewKey: z.string().regex(SLUG_PATTERN),
  })
  .strict();

export const AppNavSchema = z
  .object({
    category: z
      .object({
        id: z.string().regex(SLUG_PATTERN),
        label: z.string().min(1).max(60),
        order: z.number().int().min(0).max(1000).optional(),
      })
      .strict(),
    items: z.array(AppNavItemSchema).min(1).max(30),
  })
  .strict();

export const AppPicklistItemSchema = z
  .object({
    id: z.string().regex(PICKLIST_ITEM_ID_PATTERN),
    label: z.string().min(1).max(80),
    order: z.number().int().min(0).optional(),
    color: z.string().regex(HEX_COLOR_PATTERN).optional(),
    is_default: z.boolean().optional(),
    is_closed: z.boolean().optional(),
    is_won: z.boolean().optional(),
  })
  .strict();

export const AppPicklistSchema = z
  .object({
    name: z.string().regex(PICKLIST_NAME_PATTERN),
    items: z.array(AppPicklistItemSchema).min(1).max(100),
  })
  .strict();

export const AppManifestSchema = z
  .object({
    key: z.string().regex(APP_KEY_PATTERN, 'app keys are lower-case slugs such as projects'),
    name: z.string().min(1).max(80),
    description: z.string().max(1000).optional(),
    // An app may define no types of its own and only add views and navigation over built-in ones (the tasks template).
    /** The app's own types, and built-in types it redefines for the tenant (same key, its own fields and sections). */
    objectTypes: z.array(AppObjectTypeSchema).max(20).default([]),
    edgeTypes: z.array(AppEdgeTypeSchema).max(40).default([]),
    views: z.array(AppViewSchema).min(1).max(40),
    nav: AppNavSchema,
    picklists: z.array(AppPicklistSchema).max(40).default([]),
    /**
     * Personas the app ships: installed (source `app:<key>`) when the app is activated, upgraded with it, and disabled
     * with it. Most apps need none; the Operations Manager covers their records. One is for a job with judgement and
     * initiative, such as a Project Manager who runs a weekly review.
     */
    personas: z.array(PersonaDefinitionSchema).max(10).default([]),
  })
  .strict();

export type AppField = z.infer<typeof AppFieldSchema>;
export type AppRelationshipSection = z.infer<typeof AppRelationshipSectionSchema>;
export type AppObjectType = z.infer<typeof AppObjectTypeSchema>;
export type AppEdgeType = z.infer<typeof AppEdgeTypeSchema>;
export type AppView = z.infer<typeof AppViewSchema>;
export type AppCardRelation = z.infer<typeof AppCardRelationSchema>;
export type AppNavItem = z.infer<typeof AppNavItemSchema>;
export type AppNav = z.infer<typeof AppNavSchema>;
export type AppPicklist = z.infer<typeof AppPicklistSchema>;
export type AppPersona = PersonaDefinition;
export type AppManifest = z.infer<typeof AppManifestSchema>;
/** A manifest as written by hand or by an agent, before defaults are applied. */
export type AppManifestInput = z.input<typeof AppManifestSchema>;

/** The built-in layer a manifest is checked against; defaults to the compiled registries. */
export interface BuiltinRegistry {
  nodeTypes: Record<string, { formFields: ReadonlyArray<{ key: string; type: string }> }>;
  edgeTypes: Record<string, unknown>;
  navPaths: readonly string[];
}

export function defaultBuiltinRegistry(): BuiltinRegistry {
  return {
    nodeTypes: NODE_REGISTRY,
    edgeTypes: EDGE_REGISTRY,
    navPaths: Object.values(MODULE_REGISTRY).flatMap((module) => [...module.routes.map((route) => route.path), ...(module.dashboardPath ? [module.dashboardPath] : [])]),
  };
}

export type AppManifestValidation = { ok: true; manifest: AppManifest } | { ok: false; errors: string[] };

function formatIssues(error: z.ZodError): string[] {
  return error.issues.map((issue) => (issue.path.length > 0 ? `${issue.path.join('.')}: ${issue.message}` : issue.message));
}

/**
 * Parses and cross-checks a manifest: shape, namespacing, collisions with built-ins, and that every reference (views to
 * object types, nav to views, edges and lookups to types, kanban stage fields, sorts, filters and board filters to
 * fields) points at something that exists in this manifest or in the built-in layer. Picklists a field names are checked only when the manifest declares them;
 * a tenant may already have the others (for example task_status).
 */
export function validateAppManifest(input: unknown, builtins: BuiltinRegistry = defaultBuiltinRegistry()): AppManifestValidation {
  const parsed = AppManifestSchema.safeParse(input);
  if (!parsed.success) return { ok: false, errors: formatIssues(parsed.error) };
  const manifest = parsed.data;
  const errors: string[] = [];
  const prefix = `${APP_TYPE_PREFIX}${manifest.key}:`;

  const objectTypes = new Map<string, AppObjectType>();
  for (const objectType of manifest.objectTypes) {
    const isAppType = APP_TYPE_KEY_PATTERN.test(objectType.key);
    // An app type carries this app's prefix; any other key must be a built-in type, which the app then redefines
    if (isAppType && !objectType.key.startsWith(prefix)) errors.push(`object type "${objectType.key}" must start with "${prefix}"`);
    if (!isAppType && !(objectType.key in builtins.nodeTypes)) errors.push(`object type "${objectType.key}" is neither "${prefix}<Type>" nor a built-in type to redefine`);
    if (objectTypes.has(objectType.key)) errors.push(`duplicate object type "${objectType.key}"`);
    objectTypes.set(objectType.key, objectType);
  }

  const edgeTypes = new Map<string, AppEdgeType>();
  for (const edgeType of manifest.edgeTypes) {
    if (!edgeType.key.startsWith(prefix)) errors.push(`edge type "${edgeType.key}" must start with "${prefix}"`);
    if (edgeTypes.has(edgeType.key)) errors.push(`duplicate edge type "${edgeType.key}"`);
    if (edgeType.key in builtins.edgeTypes) errors.push(`edge type "${edgeType.key}" collides with a built-in edge type`);
    edgeTypes.set(edgeType.key, edgeType);
  }

  const knownNodeType = (type: string): boolean => type === '*' || objectTypes.has(type) || type in builtins.nodeTypes;
  const knownEdgeType = (type: string): boolean => edgeTypes.has(type) || type in builtins.edgeTypes;

  const fieldsOf = (type: string): ReadonlyArray<{ key: string; type: string }> | undefined => objectTypes.get(type)?.fields ?? builtins.nodeTypes[type]?.formFields;

  for (const edgeType of manifest.edgeTypes) {
    for (const type of [...edgeType.sourceTypes, ...edgeType.targetTypes]) {
      if (!knownNodeType(type)) errors.push(`edge type "${edgeType.key}" references unknown node type "${type}"`);
    }
  }

  const picklists = new Set(manifest.picklists.map((picklist) => picklist.name));
  if (picklists.size !== manifest.picklists.length) errors.push('duplicate picklist names');

  for (const objectType of manifest.objectTypes) {
    for (const field of objectType.fields) {
      if (field.type === 'lookup' && field.lookupConfig) {
        if (!knownNodeType(field.lookupConfig.nodeType)) errors.push(`${objectType.key}.${field.key}: unknown lookup node type "${field.lookupConfig.nodeType}"`);
        if (!knownEdgeType(field.lookupConfig.edgeType)) errors.push(`${objectType.key}.${field.key}: unknown lookup edge type "${field.lookupConfig.edgeType}"`);
      }
    }
    for (const section of objectType.relationshipSections ?? []) {
      for (const edgeType of section.edgeTypes) {
        if (!knownEdgeType(edgeType)) errors.push(`${objectType.key} section "${section.sectionLabel}": unknown edge type "${edgeType}"`);
      }
      for (const type of section.relatedNodeTypes ?? []) {
        if (!knownNodeType(type)) errors.push(`${objectType.key} section "${section.sectionLabel}": unknown node type "${type}"`);
      }
    }
  }

  const views = new Map<string, AppView>();
  for (const view of manifest.views) {
    if (views.has(view.key)) errors.push(`duplicate view "${view.key}"`);
    views.set(view.key, view);
    const fields = fieldsOf(view.objectType);
    if (!fields) {
      errors.push(`view "${view.key}" references unknown object type "${view.objectType}"`);
      continue;
    }
    const fieldKeys = new Set(fields.map((field) => field.key));
    for (const column of view.columns ?? []) {
      if (!fieldKeys.has(column)) errors.push(`view "${view.key}": column "${column}" is not a field of ${view.objectType}`);
    }
    if (view.stageField && !fieldKeys.has(view.stageField)) errors.push(`view "${view.key}": stageField "${view.stageField}" is not a field of ${view.objectType}`);
    if (view.sumField && !fieldKeys.has(view.sumField)) errors.push(`view "${view.key}": sumField "${view.sumField}" is not a field of ${view.objectType}`);
    if (view.cardRelation && !knownEdgeType(view.cardRelation.edgeType)) errors.push(`view "${view.key}": cardRelation edge type "${view.cardRelation.edgeType}" is unknown`);
    errors.push(...viewRuleErrors(`view "${view.key}"`, view.objectType, fields, view));
  }

  const reserved = new Set([...RESERVED_NAV_PATHS, ...builtins.navPaths]);
  const paths = new Set<string>();
  for (const item of manifest.nav.items) {
    if (paths.has(item.path)) errors.push(`duplicate nav path "${item.path}"`);
    paths.add(item.path);
    const root = `/${item.path.split('/')[1] ?? ''}`;
    if (reserved.has(item.path) || reserved.has(root)) errors.push(`nav path "${item.path}" is taken by a built-in route`);
    const view = views.get(item.viewKey);
    if (!view) {
      errors.push(`nav item "${item.path}" references unknown view "${item.viewKey}"`);
    } else if (view.objectType !== item.objectType) {
      errors.push(`nav item "${item.path}" is for ${item.objectType} but view "${item.viewKey}" shows ${view.objectType}`);
    }
  }

  // A persona's scope may name the app's own types and the built-ins; other apps' types are checked at activation,
  // against the merged registry, since those may come and go
  const personaSlugs = new Set<string>();
  const scopeTypes = [...objectTypes.keys(), ...Object.keys(builtins.nodeTypes)];
  for (const [index, persona] of manifest.personas.entries()) {
    if (personaSlugs.has(persona.slug)) errors.push(`duplicate persona "${persona.slug}"`);
    personaSlugs.add(persona.slug);
    const validation = validatePersonaDefinition(persona, { nodeTypes: scopeTypes });
    if (!validation.ok) errors.push(...validation.errors.map((error) => `personas.${index} (${persona.slug}): ${error}`));
    const scoped = [...persona.scope.read, ...persona.scope.write].filter((type) => type !== SCOPE_ALL);
    if (scoped.some((type) => !objectTypes.has(type) && !(type in builtins.nodeTypes))) {
      errors.push(`personas.${index} (${persona.slug}): a scope may only name this app's types and built-in types`);
    }
  }

  return errors.length > 0 ? { ok: false, errors } : { ok: true, manifest };
}
